import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  User,
  Building,
  MapPin,
  Loader2
} from 'lucide-react';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import { paymentApi } from '../lib/paymentApi';
import { getCurrentTenantConfig } from '../config/tenant';

// Types
interface PaymentLinkData {
  orderId: string;
  patientName: string;
  patientEmail: string;
  patientPhone: string;
  patientAddress: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  clinicName: string;
  items: Array<{
    productName: string;
    quantity: number;
    totalPrice: number;
  }>;
  subtotal: number;
  shippingAmount: number;
  telemedNetworkFee?: number;
  totalAmount: number;
  currency: string;
  paymentStatus: 'pending' | 'succeeded' | 'failed';
  expiresAt?: string;
}

interface HyperElementsOptions {
  publishableKey: string;
  clientSecret: string;
  loader?: 'auto' | 'always' | 'never';
  appearance?: {
    theme: 'default' | 'midnight' | 'brutal' | 'charcoal' | 'soft' | 'bubblegum' | 'none';
    variables?: Record<string, string>;
  };
}

// Error boundary for Hyperswitch SDK
class HyperswitchErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMessage: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Hyperswitch payment form failed to load:', error, errorInfo);
    Sentry.captureException(error, {
      tags: { 'payment.component': 'HyperswitchErrorBoundary', 'payment.page': 'PatientPayment' },
      extra: { componentStack: errorInfo.componentStack }
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-red-300 bg-red-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
            <h3 className="text-lg font-medium text-red-800">Payment System Unavailable</h3>
          </div>
          <p className="text-red-700 mb-4">
            We're having trouble loading the payment form. This could be due to a network issue or temporary service interruption.
          </p>
          <div className="space-y-2 text-sm text-red-600">
            <p><strong>What you can do:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Refresh this page and try again</li>
              <li>Check your internet connection</li>
              <li>Contact {getCurrentTenantConfig().branding.companyName} for assistance</li>
            </ul>
          </div>
          <div className="mt-4 pt-4 border-t border-red-200">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium min-h-[44px] transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main payment component
const PatientPaymentInner: React.FC = () => {
  const { paymentLinkId } = useParams<{ paymentLinkId: string }>();

  const [paymentData, setPaymentData] = useState<PaymentLinkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Load payment data
  useEffect(() => {
    const loadPaymentData = async () => {
      if (!paymentLinkId) {
        setError('Payment link ID is missing');
        setLoading(false);
        return;
      }

      try {
        const response = await paymentApi.getPaymentLinkDetails(paymentLinkId);
        if (!response.success) {
          throw new Error(response.error || 'Failed to load payment information');
        }
        const orderData = response.data; // Response data is directly the order object

        const transformedPaymentData: PaymentLinkData = {
          orderId: orderData._id,
          patientName: `${orderData.patientId.firstName} ${orderData.patientId.lastName}`,
          patientEmail: orderData.patientId.email || `${orderData.patientId.firstName.toLowerCase()}.${orderData.patientId.lastName.toLowerCase()}@example.com`,
          patientPhone: orderData.patientId.phone || '+1234567890',
          patientAddress: {
            street: orderData.patientId.address?.street || '123 Main St',
            street2: orderData.patientId.address?.street2 || undefined,
            city: orderData.patientId.address?.city || 'Anytown',
            state: orderData.patientId.address?.state || 'CA',
            zipCode: orderData.patientId.address?.zipCode || '12345'
          },
          clinicName: orderData.physicianId.profile?.clinicName || 'Clinic',
          items: orderData.items.map((item: any) => ({
            productName: item.productName,
            quantity: item.quantity,
            totalPrice: item.totalPrice,
          })),
          // Nullish, not falsy: a real subtotal of 0 must render as 0. The
          // total is only a stand-in for orders old enough to have no subtotal.
          subtotal: orderData.subtotal ?? orderData.totalAmount,
          shippingAmount: orderData.shippingAmount || 0,
          telemedNetworkFee: orderData.telemedNetworkFee || 0,
          totalAmount: orderData.totalAmount,
          currency: 'USD',
          paymentStatus: orderData.paymentDetails?.status === 'succeeded' ? 'succeeded' : 'pending',
          expiresAt: undefined
        };

        setPaymentData(transformedPaymentData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load payment information');
      } finally {
        setLoading(false);
      }
    };

    loadPaymentData();
  }, [paymentLinkId]);

  // Hyperswitch checkout form component
  const HyperswitchCheckoutForm: React.FC<{
    paymentData: PaymentLinkData;
    paymentLinkId: string;
  }> = ({ paymentData, paymentLinkId }) => {

    // Configure UnifiedCheckout with customer data
    const unifiedCheckoutOptions = {
      layout: { type: 'tabs' as const },
      business: { name: paymentData.clinicName },
      // Note: 'loader' option belongs in HyperElements options, not here
      // Set card as default payment method to avoid SDK warning
      paymentMethodOrder: ['card', 'google_pay', 'apple_pay'],
      fields: {
        billingDetails: {
          name: 'auto' as const,
          email: 'auto' as const,
          phone: 'auto' as const,
          address: 'auto' as const
        }
      },
      wallets: {
        walletReturnUrl: `${window.location.origin}/payment/success/${paymentLinkId}`,
        applePay: 'auto' as const,
        googlePay: 'auto' as const
      },
      sdkHandleConfirmPayment: {
        handleConfirm: true,
        buttonText: `Pay $${paymentData.totalAmount.toFixed(2)}`,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success/${paymentLinkId}`,
        },
      },
      displaySavedPaymentMethods: false
    };

    return (
      <div className="space-y-4">
        <style>{`
          /* Hide Hyperswitch branding and clean up form */
          [class*="powered"], 
          [class*="hyperswitch"], 
          [class*="Powered"],
          [data-testid*="powered"],
          [class*="footer"],
          [class*="Footer"],
          div[class*="text-center"]:has-text("powered"),
          div[class*="text-center"]:has-text("Powered"),
          span:contains("powered by"),
          span:contains("Powered by"),
          a[href*="hyperswitch"] {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            opacity: 0 !important;
          }
          
          /* Clean up any empty footer areas */
          [id*="unified-checkout"] > div:last-child:empty,
          [id*="unified-checkout"] [class*="footer"]:empty {
            display: none !important;
          }
        `}</style>
        <UnifiedCheckout 
          id="unified-checkout" 
          options={unifiedCheckoutOptions}
          onReady={() => {
            console.log('✅ Payment form loaded successfully');
            // Try to hide branding elements after load
            setTimeout(() => {
              const brandingElements = document.querySelectorAll('[class*="powered"], [class*="hyperswitch"], [class*="Powered"]');
              brandingElements.forEach(el => (el as HTMLElement).style.display = 'none');
            }, 1000);
          }}
          onError={(error: any) => {
            console.error('❌ Payment form error:', error);
            setPaymentError('Payment form failed to load. Please refresh the page and try again.');
          }}
        />
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !paymentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Link Error</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium min-h-[44px] transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if payment already completed
  if (paymentData.paymentStatus === 'succeeded') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Completed</h1>
            <p className="text-gray-600">This payment has already been processed successfully.</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if payment expired
  const isExpired = paymentData.expiresAt && new Date(paymentData.expiresAt) < new Date();
  if (isExpired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <Clock className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Link Expired</h1>
            <p className="text-gray-600 mb-4">
              This payment link has expired. Please contact {paymentData.clinicName} for a new payment link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main payment page
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Secure Payment</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Complete your prescription payment</p>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500 self-start sm:self-auto">
              <Shield className="h-4 w-4 flex-shrink-0" />
              <span>SSL Secured</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
            
            {/* Patient and Clinic Info */}
            <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Patient</p>
                  <p className="font-medium text-gray-900">{paymentData.patientName}</p>
                  <p className="text-sm text-gray-500">{paymentData.patientEmail}</p>
                  <p className="text-sm text-gray-500">{paymentData.patientPhone}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Shipping Address</p>
                  <div className="text-sm text-gray-900 space-y-0.5">
                    <p className="font-medium">{paymentData.patientAddress.street}</p>
                    {paymentData.patientAddress.street2 && (
                      <p>{paymentData.patientAddress.street2}</p>
                    )}
                    <p>{paymentData.patientAddress.city}, {paymentData.patientAddress.state} {paymentData.patientAddress.zipCode}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Prescribed by</p>
                  <p className="font-medium text-gray-900">{paymentData.clinicName}</p>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3 mb-6">
              <h3 className="font-medium text-gray-900">Prescription Items</h3>
              {paymentData.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      ${item.totalPrice.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pricing Breakdown */}
            <div className="pt-6 border-t border-gray-200 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">${paymentData.subtotal.toFixed(2)}</span>
              </div>
              {paymentData.shippingAmount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-gray-900">${paymentData.shippingAmount.toFixed(2)}</span>
                </div>
              )}
              {(paymentData.telemedNetworkFee ?? 0) > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Telemed visit fee</span>
                  <span className="text-gray-900">${(paymentData.telemedNetworkFee ?? 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <span className="text-lg font-semibold text-gray-900">Total</span>
                <span className="text-lg font-bold text-gray-900">
                  ${paymentData.totalAmount.toFixed(2)} {paymentData.currency}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h2>

            {paymentError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
                  <p className="text-red-700">{paymentError}</p>
                </div>
              </div>
            )}

            <HyperswitchErrorBoundary>
              <HyperswitchCheckoutForm 
                paymentData={paymentData} 
                paymentLinkId={paymentLinkId!} 
              />
            </HyperswitchErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component with Hyperswitch integration
export const PatientPayment: React.FC = () => {
  const { paymentLinkId } = useParams<{ paymentLinkId: string }>();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const hyperPromiseRef = useRef<Promise<any> | null>(null);
  // One-shot guard so a duplicate create-intent call (StrictMode double-effect,
  // re-render) can't spawn a second payment session (MD-2696)
  const intentRequestedRef = useRef(false);
  const tenantConfig = getCurrentTenantConfig();

  // Load Hyperswitch SDK with correct environment endpoint
  useEffect(() => {
    if (!hyperPromiseRef.current) {
      const hyperswitchEnv = tenantConfig.payments.hyperswitch.environment;
      const customBackendUrl = hyperswitchEnv === 'sandbox' 
        ? 'https://sandbox.hyperswitch.io'
        : 'https://api.hyperswitch.io';
      
      hyperPromiseRef.current = loadHyper(tenantConfig.payments.hyperswitch.publishableKey, {
        customBackendUrl
      });
    }
  }, [tenantConfig.payments.hyperswitch.publishableKey]);

  // Fetch client secret
  useEffect(() => {
    const fetchClientSecret = async () => {
      if (!paymentLinkId) return;
      if (intentRequestedRef.current) return;
      intentRequestedRef.current = true;

      try {
        const response = await fetch('/api/payments/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId: paymentLinkId }),
        });

        const data = await response.json();
        if (data.success && data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          throw new Error(data.error || 'Failed to initialize payment');
        }
      } catch (error) {
        console.error('Payment intent creation failed:', error);
        setInitError(error instanceof Error ? error.message : 'Failed to initialize payment. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchClientSecret();
  }, [paymentLinkId]);

  // Show error if payment initialization failed
  if (initError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Unavailable</h1>
            <p className="text-gray-600 mb-4">{initError}</p>
            <p className="text-sm text-gray-500 mb-4">
              Please contact {tenantConfig.branding.companyName} for assistance with your payment.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium min-h-[44px] transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !clientSecret || !hyperPromiseRef.current) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Initializing secure payment...</p>
        </div>
      </div>
    );
  }

  const hyperswitchOptions: HyperElementsOptions = {
    publishableKey: tenantConfig.payments.hyperswitch.publishableKey,
    clientSecret,
    loader: 'auto',  // Display skeleton UI while loading
    appearance: {
      theme: 'default',
      variables: {
        colorPrimary: '#3B82F6',
        colorBackground: '#ffffff',
        colorText: '#374151',
        borderRadius: '8px'
      }
    }
  };



  return (
    <HyperElements options={hyperswitchOptions} hyper={hyperPromiseRef.current}>
      <PatientPaymentInner />
    </HyperElements>
  );
};